/**
 * Kits (ADR-63 Cut 2): the template (a bill of materials, versioned) and the
 * instance (a real assembled kit with its own HN-CODE identity).
 *
 * Two rules the vocabulary carries so the runtime cannot forget them:
 *
 *  - A template VERSION is immutable once instances exist (design section 38).
 *    A change is a new version; an instance keeps the version it was assembled
 *    from. Versioning is a (code, version) pair, not an edit in place.
 *
 *  - Readiness is DERIVED from membership against the template BOM, never
 *    stored (engineering rule 11): a kit is complete when every required line
 *    is met, and the moment a cable is pulled it is not, without anyone
 *    writing a flag. `computeKitReadiness` is that pure function; inspection
 *    and maintenance states join it in later cuts.
 *
 * Kit membership is the fourth relationship, separate from ownership,
 * assignment and custody: an asset can belong to a kit that is owned by
 * HipNumber and assigned to a tenant, three different facts.
 */

export const KIT_INSTANCE_STATUSES = ['ASSEMBLING', 'IN_SERVICE', 'RETIRED'] as const;
export type KitInstanceStatus = (typeof KIT_INSTANCE_STATUSES)[number];

export const KIT_INSTANCE_STATUS_LABEL: Readonly<Record<KitInstanceStatus, string>> = {
  ASSEMBLING: 'Assembling',
  IN_SERVICE: 'In service',
  RETIRED: 'Retired',
};

/** Operator-initiated kit lifecycle. RETIRED is final. */
export const KIT_INSTANCE_TRANSITIONS: Readonly<Record<KitInstanceStatus, readonly KitInstanceStatus[]>> = {
  ASSEMBLING: ['IN_SERVICE', 'RETIRED'],
  IN_SERVICE: ['ASSEMBLING', 'RETIRED'],
  RETIRED: [],
};

export function canTransitionKit(from: KitInstanceStatus, to: KitInstanceStatus): boolean {
  return KIT_INSTANCE_TRANSITIONS[from].includes(to);
}

export const KIT_READINESS = ['READY', 'INCOMPLETE', 'NEEDS_INSPECTION', 'NEEDS_MAINTENANCE', 'OUT_OF_SERVICE'] as const;
export type KitReadiness = (typeof KIT_READINESS)[number];

export const KIT_READINESS_LABEL: Readonly<Record<KitReadiness, string>> = {
  READY: 'Ready',
  INCOMPLETE: 'Incomplete',
  NEEDS_INSPECTION: 'Needs inspection',
  NEEDS_MAINTENANCE: 'Needs maintenance',
  OUT_OF_SERVICE: 'Out of service',
};

/**
 * How a template line is fulfilled in an instance. SERIALIZED means the line is
 * filled by member assets (counted by matching catalog item); QUANTITY means by
 * a counted component; EITHER accepts whichever the catalog item is.
 */
export const TRACKING_REQUIREMENTS = ['SERIALIZED', 'QUANTITY', 'EITHER'] as const;
export type TrackingRequirement = (typeof TRACKING_REQUIREMENTS)[number];

/** One BOM line's fulfillment, the unit `computeKitReadiness` reasons over. */
export interface KitLine {
  readonly required: boolean;
  readonly requiredQuantity: number;
  readonly actualQuantity: number;
}

export function kitLineMet(line: KitLine): boolean {
  return line.actualQuantity >= line.requiredQuantity;
}

/**
 * Signals a kit's readiness depends on beyond its parts. Both default off, so a
 * caller that knows nothing about them gets pure completeness. Cut 4 supplies
 * `hasOpenMaintenance`; `needsInspection` is Cut 5's to fill.
 */
export interface KitReadinessSignals {
  readonly hasOpenMaintenance?: boolean;
  readonly needsInspection?: boolean;
}

/**
 * Readiness, in order of precedence: a retired kit is OUT_OF_SERVICE; an
 * unresolved maintenance event makes it NEEDS_MAINTENANCE (something is actively
 * broken, more urgent than a missing part); a short required line is INCOMPLETE;
 * an outstanding required inspection is NEEDS_INSPECTION; otherwise READY.
 * Every input is passed in; nothing is guessed here.
 */
export function computeKitReadiness(status: KitInstanceStatus, lines: readonly KitLine[], signals: KitReadinessSignals = {}): KitReadiness {
  if (status === 'RETIRED') return 'OUT_OF_SERVICE';
  if (signals.hasOpenMaintenance) return 'NEEDS_MAINTENANCE';
  const shortRequired = lines.some((l) => l.required && !kitLineMet(l));
  if (shortRequired) return 'INCOMPLETE';
  if (signals.needsInspection) return 'NEEDS_INSPECTION';
  return 'READY';
}

// -- kit instance identity -------------------------------------------------
// HN-RING-004, HN-REG-008: the prefix is the template CODE, the number is
// per-code sequential (minted by kit_code_counters in migration 0072). Three
// digits is a display minimum; lpad never truncates.

export const KIT_CODE_WIDTH = 3;

/**
 * `AST` is the asset-tag prefix (HN-AST-000001) and is reserved: a kit code may
 * not be AST, so the kit-id and asset-tag namespaces are disjoint and a scanned
 * HN-AST tag can never parse as a kit id.
 */
export const RESERVED_KIT_CODES: readonly string[] = ['AST'];

/** A template code is an uppercase token (RING, REG, CHK), not the reserved AST. */
export function isKitCode(code: string): boolean {
  return /^[A-Z][A-Z0-9]{0,15}$/.test(code) && !RESERVED_KIT_CODES.includes(code);
}

export function formatKitCode(code: string, seq: number): string {
  if (!isKitCode(code)) throw new RangeError(`kit code must be an uppercase token and not reserved, got ${code}`);
  if (!Number.isInteger(seq) || seq < 1) throw new RangeError(`kit sequence must be a positive integer, got ${seq}`);
  return `HN-${code}-${String(seq).padStart(KIT_CODE_WIDTH, '0')}`;
}

const KIT_CODE_RE = /^HN-([A-Z][A-Z0-9]*)-(\d{3,})$/;

export function parseKitCode(s: string): { code: string; seq: number } | null {
  const m = KIT_CODE_RE.exec(s.trim().toUpperCase());
  if (!m || !isKitCode(m[1]!)) return null;
  const seq = Number(m[2]);
  return Number.isSafeInteger(seq) && seq >= 1 ? { code: m[1]!, seq } : null;
}
